import { unwrapResult } from "@reduxjs/toolkit";
import { JobDetailsSkeleton } from "components/job/JobDetailsSkeleton";
import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setPage, setSelectedJob } from "store/root.store";
import { getPartnerCachePrefill, getPartsTechnician } from "store/slices/cache.store";
import { clearHistoryState, setHistorySelectedJob } from "store/slices/history.store";
import {
  getComments,
  getFiles,
  getNotes,
  getPartnerJob,
  selectJobLoaded,
  selectVisitLoaded,
  setJobTab,
  setPartnerToken,
} from "store/slices/partner.store";
import { clearPlannerState, setPlannerSelectedJob } from "store/slices/planner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { getPartnerData, getPermissionSettings, isFlagEnabled } from "store/slices/user.store";

interface PartnerProviderProps {
  token: string | undefined;
}

export default function PartnerProvider({ token }: PartnerProviderProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token: partnerToken, loadingJob, selectedJobId } = useAppSelector((s) => s.partner);
  const visitLoaded = useAppSelector(selectVisitLoaded);
  const jobLoaded = useAppSelector(selectJobLoaded);
  const commentsEnabled = useAppSelector((s) => isFlagEnabled(s, "PartnerComments"));

  useEffect(() => {
    if (!token) return;
    dispatch(setPage({ page: "partner" }));
    dispatch(setPartnerToken({ token }));
    dispatch(setSelectedJob({ jobId: undefined }));
    dispatch(setJobTab({ tab: "details" }));
    dispatch(setPlannerSelectedJob({ jobId: undefined }));
    dispatch(setHistorySelectedJob({ jobId: undefined }));
    dispatch(clearPlannerState());
    dispatch(clearHistoryState());
  }, [dispatch, token]);

  useEffect(() => {
    if (!partnerToken) return;
    dispatch(getPartnerData())
      .then(unwrapResult)
      .then(({ data }) => {
        if (!data.partnerData) return;
        dispatch(getPermissionSettings());
        dispatch(getPartnerCachePrefill({ force: false }));
        dispatch(getPartsTechnician({ force: false }));
        dispatch(setSelectedJob({ jobId: data.partnerData?.jobId }));
        dispatch(getPartnerJob(data.partnerData.jobId)).catch(() => {
          dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
        });
      })
      .catch((reason) => {
        console.log(reason[0].message);
        navigate("/login", { replace: true });
        if (reason && reason[0].message === "Invalid bearer token") {
          dispatch(addSnackbarMessage({ key: "PartnerToken-invalid" }));
        }
      });
  }, [dispatch, navigate, partnerToken]);

  useEffect(() => {
    if (!selectedJobId) return;
    dispatch(getFiles());
    dispatch(getNotes());
    if (commentsEnabled) dispatch(getComments());
  }, [commentsEnabled, dispatch, selectedJobId]);

  if (!partnerToken || loadingJob || !jobLoaded || !visitLoaded) {
    return <JobDetailsSkeleton />;
  }

  return <Outlet />;
}
